import { useEffect, useRef, useState } from 'react';
import { ContentfulEntry } from '@jbhi-fi/apis/src/contentful/types';
import { usePublicApiClient } from '../usePublicApiClient';

export type ContentfulFilter = Record<string, string> & { content_type?: string };

export type UseContentfulEntriesProps = {
  filter: ContentfulFilter;
  count?: number;
  skip?: number;
  linkedEntryDepth?: number;
  autoRefresh?: boolean;
};

export type UseContentfulEntries<T> = {
  entries?: Array<T> | null;
  totalResults: number;
  isLoading: boolean;
  error?: string;
};

export const useContentfulEntries = <T extends ContentfulEntry<any>>({
  filter,
  count = 1,
  skip = 0,
  linkedEntryDepth = 6,
  autoRefresh = false,
}: UseContentfulEntriesProps): UseContentfulEntries<T> => {
  const { contentfulThemeApiClient, contentfulClient } = usePublicApiClient();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [entries, setEntries] = useState<Array<T> | null>(null);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [error, setError] = useState<string | undefined>();
  const refreshInterval = useRef<number>();
  const isPreview = window.Jbhifi?.isContentPreview || false;

  if (!contentfulThemeApiClient || !contentfulClient) {
    throw new Error('useContentfulEntries must be called within a PublicApiClient provider');
  }
  const queryParams = {
    ...filter,
    limit: count.toString(),
    include: linkedEntryDepth.toString(),
    skip: skip.toString(),
  };

  const fetchData = async () => {
    try {
      const shouldUseThemeApi = isPreview || !contentfulClient;
      const client = shouldUseThemeApi ? contentfulThemeApiClient : contentfulClient;
      if (!client) {
        throw new Error('Contentful client unavailable');
      }
      const response = await client.getEntries<T>(queryParams);
      setEntries(currentEntries => (currentEntries ?? []).concat((response.data?.items ?? []) as Array<T>));
      setTotalResults(response.data?.total ?? totalResults);
    } catch (e) {
      console.error(e);
      setError('Error fetching entries');
    } finally {
      setIsLoading(false);
    }
  };

  const reloadEntries = async (entries: Array<T>) => {
    try {
      const shouldUseThemeApi = isPreview || !contentfulClient;
      const client = shouldUseThemeApi ? contentfulThemeApiClient : contentfulClient;
      if (!client) {
        throw new Error('Contentful client unavailable');
      }
      const result = await client.getEntries<T>({
        content_type: filter.content_type ?? '',
        include: linkedEntryDepth.toString(),
        'sys.id[in]': entries?.map(entry => entry?.sys?.id)?.join(','),
      });
      setEntries((result.data?.items ?? []) as unknown as Array<T>);
    } catch (e) {
      console.error('Error refreshing content', e);
    }
  };

  useEffect(() => {
    if (refreshInterval.current) clearInterval(refreshInterval.current);
    setIsLoading(true);
    fetchData();
  }, [skip]);

  useEffect(() => {
    if (!autoRefresh || !entries?.length) return;

    refreshInterval.current = window.setInterval(() => reloadEntries(entries), 2500);
    return () => (refreshInterval.current ? clearInterval(refreshInterval.current) : undefined);
  }, [entries]);

  return { isLoading, entries, totalResults, error };
};
